import React, { useMemo, useState } from 'react';
import { StockCategoryEntity, StockEntity, StockPlaceEntity } from '../types/data';
import Icon from './icons/Icon';
import './StockMenu.css';

interface StockMenuProps {
  categories: StockCategoryEntity[];
  places: StockPlaceEntity[];
  stocks: StockEntity[];
  viewMode: 'category' | 'place';
  selectedGroupId: string | null;
  onSelect?: (groupId: string | null) => void;
  onReorderCategories?: (sourceId: string, targetId: string, insertAfter: boolean) => void | Promise<void>;
  onReorderPlaces?: (sourceId: string, targetId: string, insertAfter: boolean) => void | Promise<void>;
}

const StockMenu: React.FC<StockMenuProps> = ({
  categories,
  places,
  stocks,
  viewMode,
  selectedGroupId,
  onSelect,
  onReorderCategories,
  onReorderPlaces,
}) => {
  const [draggingGroupId, setDraggingGroupId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState<{ id: string; insertAfter: boolean } | null>(null);

  const sortedCategories = useMemo(() => {
    return [...categories].sort((a, b) => {
      const ao = a.sort_order ?? 0;
      const bo = b.sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return at - bt;
    });
  }, [categories]);

  const sortedPlaces = useMemo(() => {
    return [...places].sort((a, b) => {
      const ao = a.sort_order ?? 0;
      const bo = b.sort_order ?? 0;
      if (ao !== bo) return ao - bo;
      const at = new Date(a.created_at).getTime();
      const bt = new Date(b.created_at).getTime();
      return at - bt;
    });
  }, [places]);

  const getCount = useMemo(() => {
    return (groupId: string | null): number => {
      if (viewMode === 'place') {
        if (groupId === null) {
          return stocks.filter(s => !s.is_deleted && !s.stock_place_id).length;
        }
        return stocks.filter(s => !s.is_deleted && s.stock_place_id === groupId).length;
      }
      if (groupId === null) {
        return stocks.filter(s => !s.is_deleted && !s.stock_category_id).length;
      }
      return stocks.filter(s => !s.is_deleted && s.stock_category_id === groupId).length;
    };
  }, [stocks, viewMode]);

  const sortedGroups = viewMode === 'place' ? sortedPlaces : sortedCategories;
  const canReorder = viewMode === 'place' ? Boolean(onReorderPlaces) : Boolean(onReorderCategories);
  const uncategorizedLabel = viewMode === 'place' ? '未設定' : '未分類';
  const uncategorizedIcon = viewMode === 'place' ? 'location' : 'empty';
  const groupIcon = viewMode === 'place' ? 'location' : 'folder';

  const handleClick = (groupId: string | null) => {
    onSelect?.(groupId);
  };

  return (
    <div className="stock-menu">
      <div className={`stock-menu-item ${selectedGroupId === null ? 'selected' : ''}`} onClick={() => handleClick(null)} role="button">
        <div className="stock-menu-item-content">
          <div className="stock-menu-left">
            <Icon name={uncategorizedIcon} size={20} />
            <span className="stock-menu-title text-single-large">{uncategorizedLabel}</span>
          </div>
          {(() => {
            const c = getCount(null);
            return c > 0 ? <span className="stock-menu-count text-single-large">{c}</span> : null;
          })()}
        </div>
      </div>

      {sortedGroups.map(group => (
        <div
          key={group.id}
          className={[
            'stock-menu-item',
            selectedGroupId === group.id ? 'selected' : '',
            dragOver?.id === group.id ? (dragOver.insertAfter ? 'drag-over-after' : 'drag-over-before') : '',
            draggingGroupId === group.id ? 'dragging' : '',
          ].filter(Boolean).join(' ')}
          onClick={() => handleClick(group.id)}
          role="button"
          draggable={canReorder}
          aria-grabbed={draggingGroupId === group.id}
          onDragStart={(e) => {
            if (!canReorder) return;
            e.dataTransfer.effectAllowed = 'move';
            setDraggingGroupId(group.id);
          }}
          onDragEnd={() => {
            setDraggingGroupId(null);
            setDragOver(null);
          }}
          onDragOver={(e) => {
            if (!canReorder) return;
            if (!draggingGroupId) return;
            if (draggingGroupId === group.id) return;
            e.preventDefault();
            const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
            const insertAfter = (e.clientY - rect.top) > rect.height / 2;
            setDragOver({ id: group.id, insertAfter });
          }}
          onDragLeave={() => {
            if (dragOver?.id === group.id) setDragOver(null);
          }}
          onDrop={async (e) => {
            if (!canReorder) return;
            if (!draggingGroupId) return;
            if (draggingGroupId === group.id) return;
            e.preventDefault();
            const insertAfter = dragOver?.id === group.id ? dragOver.insertAfter : false;
            setDragOver(null);
            try {
              if (viewMode === 'place') {
                await Promise.resolve(onReorderPlaces?.(draggingGroupId, group.id, insertAfter));
              } else {
                await Promise.resolve(onReorderCategories?.(draggingGroupId, group.id, insertAfter));
              }
            } finally {
              setDraggingGroupId(null);
            }
          }}
        >
          <div className="stock-menu-item-content">
            <div className="stock-menu-left">
              <Icon name={groupIcon} size={20} />
              <span className="stock-menu-title text-single-large">{group.title}</span>
            </div>
            {(() => {
              const c = getCount(group.id);
              return c > 0 ? <span className="stock-menu-count text-single-large">{c}</span> : null;
            })()}
          </div>
        </div>
      ))}
    </div>
  );
};

export default StockMenu;
